import { createSharedComposable } from '@vueuse/core';

function _useSegmentReader () {
  const userSegments = ref<string[]>([]);
  const isLoading = ref(true);
  const error = ref<string | null>(null);

  const openSegmentDB = (): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open('dngroup-segments');

      request.onsuccess = (event) => {
        const db = event.target.result;
        resolve(db);
      };

      request.onerror = (event) => {
        reject(event.target.error);
      };

      request.onblocked = () => {
        reject(new Error('IndexedDB open blocked'));
      };
    });
  };

  const readUserSegments = async (): Promise<string[]> => {
    try {
      if (!import.meta.client) {
        return [];
      }

      const db = await openSegmentDB();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction('segments-info', 'readonly');
        const objectStore = transaction.objectStore('segments-info');
        const request = objectStore.get('segments');

        request.onsuccess = (event) => {
          const result = event.target.result;
          const segments = result?.value || [];
          resolve(Array.isArray(segments) ? segments : []);
          db.close();
        };

        request.onerror = (event) => {
          reject(event.target.error);
          db.close();
        };
      });
    } catch (e) {
      error.value = (e as Error).message;
      console.warn('useSegmentReader: Failed to read segments from IndexedDB', e);
      return [];
    }
  };

  const checkSegmentMatch = (segments: string[], targetSegments: string[]): string | null => {
    for (const target of targetSegments) {
      if (segments.includes(target)) {
        return target;
      }
    }
    return null;
  };

  onMounted(async () => {
    isLoading.value = true;
    try {
      const segments = await readUserSegments();
      userSegments.value = segments;
    } catch (e) {
      error.value = (e as Error).message;
      console.warn('useSegmentReader: Error during segment initialization', e);
    } finally {
      isLoading.value = false;
    }
  });

  return {
    userSegments: readonly(userSegments),
    isLoading: readonly(isLoading),
    error: readonly(error)
  };
}

export const useSegmentReader = createSharedComposable(_useSegmentReader);
